import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Injector, LOCALE_ID, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { environment } from '../environments/environment';
import { SharedModuleModule } from './shared-module/shared-module.module';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { LOCATION_INITIALIZED, registerLocaleData } from '@angular/common';
import { ModalInfoComponent } from './components/modal-info/modal-info.component';
import { WorkingHoursModalComponent } from './components/working-hours-modal/working-hours-modal.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { VenuePageModule } from './pages/venue/venue.module';
import { OrderPageModule } from './pages/order/order.module';
import { MyOrdersPageModule } from './pages/my-orders/my-orders.module';
import { EmailConfirmationPageModule } from './pages/email-confirmation/email-confirmation.module';
import { AccountPageModule } from './pages/account/account.module';
import { ModalNavigationGuard } from './modal-navigation-guard';
import { ShortInfoModalComponent } from './components/short-info-modal/short-info-modal.component';
import { RecommendationModalComponent } from './components/recommendation-modal/recommendation-modal.component';
import { PaymentModalComponent } from '../smoothr-web-app-core/components/payment-modal/payment-modal.component';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { DeliveryNotAvailableModalComponent } from './components/delivery-not-available-modal/delivery-not-available-modal.component';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireAnalyticsModule, COLLECTION_ENABLED } from '@angular/fire/compat/analytics';
import { CheckOrderGuard } from './guard/check-order-guard';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import { PaymentCancelledModalComponent } from './components/payment-cancelled-modal/payment-cancelled-modal.component';
import { CheckingPaymentModalComponent } from './components/checking-payment-modal/checking-payment-modal.component';

import { ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';

import * as Sentry from '@sentry/angular';

const config: SocketIoConfig = {
	url: environment.baseUrl,
	options: {
		path: '/v1/socketio/socket.io/',
	},
};

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

export function appInitializerFactory(translate: TranslateService, injector: Injector) {
	return () =>
		new Promise<any>((resolve: any) => {
			const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
			locationInitialized.then(() => {
				translate.use(translate.defaultLang).subscribe(
					() => {},
					() => {},
					() => resolve(null)
				);
			});
		});
}

export function createTranslateLoader(http: HttpClient) {
	return new TranslateHttpLoader(http);
}

@NgModule({
	imports: [
		BrowserModule,
		IonicModule.forRoot({
			spinner: 'crescent',
			swipeBackEnabled: false,
			scrollAssist: false,
			mode: 'md',
		}),
		AppRoutingModule,
		SharedModuleModule,
		AngularFireModule.initializeApp(environment.firebaseConfig),
		AngularFireAuthModule,
		AngularFireAnalyticsModule,
		SocketIoModule.forRoot(config),
		HttpClientModule,
		NoopAnimationsModule,
		FormsModule,
		EmailConfirmationPageModule,
		OrderPageModule,
		AccountPageModule,
		VenuePageModule,
		ReactiveFormsModule,
		FormsModule,
		MyOrdersPageModule,
		HttpClientModule,
		TranslateModule.forRoot({
			defaultLanguage: 'de',
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader,
				deps: [HttpClient],
			},
		}),
	],
	providers: [
		ModalNavigationGuard,
		CheckOrderGuard,
		{
			provide: APP_INITIALIZER,
			useFactory: appInitializerFactory,
			deps: [TranslateService, Injector],
			multi: true,
		},
		{ provide: LOCALE_ID, useValue: 'de-DE' },
		{ provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
		{ provide: COLLECTION_ENABLED, useValue: false },
		{ provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 2500 } },
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				// TODO: turn back on again, if we are sure that
				// we don't have non-severe errors triggering the feedback popup
				showDialog: false,
			}),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
	],
	declarations: [
		AppComponent,
		ModalInfoComponent,
		WorkingHoursModalComponent,
		ShortInfoModalComponent,
		DeliveryNotAvailableModalComponent,
		RecommendationModalComponent,
		PaymentModalComponent,
		PaymentCancelledModalComponent,
		CheckingPaymentModalComponent,
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
